// The product fields an import can fill, with the header words shops actually
// use (English, Romanised Nepali and Devanagari). Headers are compared after
// normalizeHeader(), so synonyms are lowercase and punctuation-free.
import { normalizeHeader } from './normalize.ts'

export type FieldKey =
  | 'sku'
  | 'name'
  | 'barcode'
  | 'category'
  | 'unit'
  | 'sell_price'
  | 'cost_price'
  | 'vat_rate'
  | 'price_includes_vat'
  | 'opening_stock'
  | 'min_stock'
  | 'description'
  | 'brand'
  | 'manufacturer'
  | 'mrp'
  | 'hs_code'
  | 'shelf_location'
  | 'tags'
  | 'batch_no'
  | 'mfg_date'
  | 'expiry_date'
  | 'group_code'
  | 'variant'
  | 'wholesale_price'
  | 'min_price'
  | 'plu_code'

/** A products.attributes field (medicine details, custom fields…): `attr:<key>`. */
export type AttrKey = `attr:${string}`
export type MapKey = FieldKey | AttrKey

export const attrKey = (key: string): AttrKey => `attr:${key}`
export const isAttrKey = (key: string): key is AttrKey => key.startsWith('attr:')

export interface FieldDef {
  key: MapKey
  label: string
  required?: boolean
  hint?: string
  synonyms: string[]
  /** Words that rule out a partial match ("product code" is not a name). Exact matches still count. */
  avoid?: string[]
}

export const FIELDS: FieldDef[] = [
  {
    key: 'name',
    label: 'Name',
    required: true,
    avoid: ['code', 'no', 'number', 'id', 'sku', 'barcode', 'qty', 'price', 'rate'],
    synonyms: [
      'name',
      'product',
      'product name',
      'item',
      'item name',
      'items',
      'particulars',
      'particular',
      'description of goods',
      'goods',
      'part name',
      'parts name',
      'stock item',
      'maal',
      'saman',
      'samaan',
      'naam',
      'नाम',
      'सामान',
      'माल',
      'विवरण',
    ],
  },
  {
    key: 'sku',
    label: 'SKU / code',
    hint: 'Left empty, a code is made from the name.',
    avoid: ['name', 'barcode', 'ean', 'upc', 'location', 'bin', 'lot', 'hs', 'hsn'],
    synonyms: [
      'sku',
      'code',
      'item code',
      'product code',
      'part no',
      'part number',
      'parts number',
      'part',
      'item no',
      'article',
      'article no',
      'style',
      'कोड',
    ],
  },
  {
    key: 'barcode',
    label: 'Barcode',
    synonyms: ['barcode', 'bar code', 'barcode no', 'ean', 'ean13', 'upc', 'gtin', 'बारकोड'],
  },
  {
    key: 'sell_price',
    label: 'Selling price',
    hint: 'Products without a price start at 0.',
    avoid: [
      'cost',
      'purchase',
      'buying',
      'pct',
      'percent',
      '%',
      'vat',
      'tax',
      'discount',
      'margin',
      'mrp',
      'wholesale',
    ],
    synonyms: [
      'selling price',
      'sell price',
      'sales price',
      'sale price',
      'retail price',
      'price',
      'rate',
      'sp',
      'dar',
      'mulya',
      'mool',
      'दर',
      'मूल्य',
      'मुल्य',
      'बिक्री मूल्य',
    ],
  },
  {
    key: 'cost_price',
    label: 'Cost price',
    avoid: ['avg', 'average', 'total'],
    synonyms: [
      'cost price',
      'cost',
      'purchase price',
      'purchase rate',
      'buying price',
      'cp',
      'landed cost',
      'kharid',
      'kharid dar',
      'खरिद',
      'खरिद मूल्य',
      'लागत',
    ],
  },
  {
    key: 'vat_rate',
    label: 'VAT %',
    avoid: ['id', 'no', 'number', 'pan', 'code', 'amount'],
    synonyms: ['vat', 'vat %', 'vat rate', 'tax', 'tax %', 'tax rate', 'भ्याट', 'कर'],
  },
  {
    key: 'price_includes_vat',
    label: 'Price includes VAT',
    synonyms: [
      'price includes vat',
      'includes vat',
      'inclusive of vat',
      'vat inclusive',
      'incl vat',
      'with vat',
    ],
  },
  {
    key: 'opening_stock',
    label: 'Opening stock',
    hint: 'Only for new products.',
    avoid: [
      'reorder',
      'min',
      'minimum',
      'max',
      'maximum',
      'reserved',
      'ordered',
      'sold',
      'lot',
      'serial',
      'serials',
      'lots',
    ],
    synonyms: [
      'opening stock',
      'opening',
      'opening qty',
      'stock',
      'in stock',
      'qty',
      'quantity',
      'balance',
      'balance qty',
      'closing stock',
      'on hand',
      'current stock',
      'matra',
      'मात्रा',
      'परिमाण',
      'स्टक',
    ],
  },
  {
    key: 'unit',
    label: 'Unit',
    synonyms: ['unit', 'units', 'uom', 'unit of measure', 'ekai', 'एकाइ', 'इकाई'],
  },
  {
    key: 'category',
    label: 'Category',
    avoid: ['id', 'code', 'parent'],
    synonyms: [
      'category',
      'group',
      'item group',
      'product group',
      'stock group',
      'type',
      'department',
      'brand',
      'वर्ग',
      'समूह',
    ],
  },
  {
    key: 'min_stock',
    label: 'Low-stock alert',
    avoid: ['qty'],
    synonyms: [
      'low stock alert',
      'low stock',
      'min stock',
      'minimum stock',
      'reorder level',
      'reorder point',
      'reorder',
      'alert',
    ],
  },
  {
    key: 'description',
    label: 'Description',
    synonyms: ['description', 'remarks', 'remark', 'notes', 'note', 'details', 'specification'],
  },
  {
    key: 'brand',
    label: 'Brand',
    avoid: ['id', 'code'],
    synonyms: ['brand', 'brand name', 'make', 'company', 'ब्रान्ड', 'कम्पनी'],
  },
  {
    key: 'manufacturer',
    label: 'Manufacturer',
    avoid: ['date', 'dt'],
    synonyms: ['manufacturer', 'mfr', 'mfg by', 'manufactured by', 'maker', 'उत्पादक'],
  },
  {
    key: 'mrp',
    label: 'MRP',
    hint: 'With no price column, MRP is also the selling price.',
    synonyms: [
      'mrp',
      'mrp with vat',
      'mrp incl vat',
      'maximum retail price',
      'max retail price',
      'printed price',
    ],
  },
  {
    key: 'hs_code',
    label: 'HS code',
    synonyms: ['hs code', 'hs', 'hsn', 'hsn code', 'hs no', 'tariff code'],
  },
  {
    key: 'shelf_location',
    label: 'Shelf / rack',
    synonyms: [
      'shelf',
      'shelf rack',
      'shelf location',
      'rack',
      'rack no',
      'bin',
      'location in store',
      'दराज',
    ],
  },
  {
    key: 'tags',
    label: 'Tags',
    synonyms: ['tags', 'tag', 'keywords', 'labels'],
  },
  {
    key: 'batch_no',
    label: 'Batch no.',
    hint: 'New products only: the opening stock goes into this batch.',
    synonyms: ['batch', 'batch no', 'batch number', 'lot', 'lot no', 'lot number', 'ब्याच'],
  },
  {
    key: 'mfg_date',
    label: 'Mfg date',
    synonyms: [
      'mfg date',
      'mfg',
      'mfd',
      'manufacture date',
      'manufacturing date',
      'manufactured on',
      'date of manufacture',
      'उत्पादन मिति',
    ],
  },
  {
    key: 'expiry_date',
    label: 'Expiry date',
    synonyms: [
      'expiry',
      'expiry date',
      'exp',
      'exp date',
      'expires',
      'expiration',
      'expiration date',
      'best before',
      'use by',
      'म्याद',
      'म्याद सकिने मिति',
    ],
  },
  {
    key: 'group_code',
    label: 'Variant of (main SKU)',
    hint: 'Rows with the same main SKU become variants of that product.',
    synonyms: [
      'group code',
      'group sku',
      'parent',
      'parent sku',
      'parent code',
      'product code',
      'main sku',
      'base sku',
      'master sku',
      'style code',
      'variant of',
      'model code',
    ],
  },
  {
    key: 'variant',
    label: 'Variant',
    hint: 'Like “Size: M, Color: Red”, “M / Red” or {"size": "M"}.',
    avoid: ['id', 'code', 'sku'],
    synonyms: [
      'variant',
      'variant name',
      'variation',
      'options',
      'attributes',
      'size colour',
      'size color',
    ],
  },
  {
    key: 'wholesale_price',
    label: 'Wholesale price',
    synonyms: [
      'wholesale',
      'wholesale price',
      'wholesale rate',
      'dealer price',
      'trade price',
      'bulk price',
      'होलसेल',
      'थोक',
      'थोक मूल्य',
    ],
  },
  {
    key: 'min_price',
    label: 'Minimum price',
    synonyms: ['min price', 'minimum price', 'lowest price', 'floor price', 'min selling price'],
  },
  {
    key: 'plu_code',
    label: 'Scale item code (PLU)',
    synonyms: ['plu', 'plu code', 'plu no', 'scale code', 'scale item code', 'scale plu'],
  },
]

export const FIELD_BY_KEY = Object.fromEntries(FIELDS.map((f) => [f.key, f])) as Record<
  FieldKey,
  FieldDef
>

/** Column index in the sheet for each mapped field. */
export type Mapping = Partial<Record<MapKey, number>>

/** Extra header words for the built-in attribute fields. */
const ATTRIBUTE_SYNONYMS: Record<string, string[]> = {
  generic_name: ['generic', 'generic name', 'composition', 'salt', 'molecule', 'जेनेरिक'],
  strength: ['strength', 'potency', 'dose', 'mg'],
  dosage_form: ['dosage form', 'form', 'formulation', 'type of medicine'],
  prescription_required: ['prescription', 'prescription required', 'rx', 'rx required', 'schedule'],
  material: ['material', 'fabric', 'कपडा'],
  season: ['season', 'मौसम'],
  model_no: ['model', 'model no', 'model number'],
}

/** Import fields for the attribute fields a business shows (its groups and custom fields). */
export function attributeFieldDefs(fields: readonly { key: string; label: string }[]): FieldDef[] {
  return fields.map((f) => ({
    key: attrKey(f.key),
    label: f.label,
    synonyms: [
      ...new Set([
        normalizeHeader(f.label),
        f.key.replace(/_/g, ' '),
        ...(ATTRIBUTE_SYNONYMS[f.key] ?? []),
      ]),
    ].filter(Boolean),
  }))
}

/** A field's label, for built-in and attribute fields alike. */
export function fieldLabel(key: MapKey, extra: readonly FieldDef[] = []): string {
  return (
    (FIELD_BY_KEY as Record<string, FieldDef | undefined>)[key]?.label ??
    extra.find((f) => f.key === key)?.label ??
    key.replace(/^attr:/, '').replace(/_/g, ' ')
  )
}
